import { useState } from "react";
import styled from "styled-components";
import {
  Button,
  Modal,
  SelectInput,
  Switch,
  Tabs,
  TextInput
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/core-labels";
import type {
  FeeManagerConfig,
  FeeManagerConfigFieldKey,
  FeeManagerTenantSchema
} from "@dealer-center/core-feemanager-interface";
import {
  FEE_SOURCE_COMMON_ORG,
  FEE_SOURCE_DDC,
  isCommonOrgSource
} from "../utils/feeSource";

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;

  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 48px;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 24px;
  }
`;

const SwitchRow = styled.div`
  margin-bottom: 16px;

  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const TabContent = styled.div`
  margin-top: 16px;
`;

const StyledModal = styled(Modal)`
  .ids-modal-title {
    margin-bottom: 16px;
    padding: 24px 24px 0 24px !important;
  }

  @media (max-width: 768px) {
    .ids-modal-title {
      padding: 16px 16px 0 16px !important;
    }
    .ids-modal-content {
      padding: 16px !important;
    }
  }

  @media (max-width: 480px) {
    .ids-modal-title {
      padding: 12px 12px 0 12px !important;
    }
    .ids-modal-content {
      padding: 12px !important;
    }
  }
`;

const SUB_TOGGLE_KEYS: readonly FeeManagerConfigFieldKey[] = [
  "NEW_saveTotalFees",
  "NEW_saveItemizedFees",
  "NEW_savePriceAfterFees",
  "USED_saveTotalFees",
  "USED_saveItemizedFees",
  "USED_savePriceAfterFees"
];

export interface ConfigurationModalProps {
  config: FeeManagerConfig;
  schema: FeeManagerTenantSchema;
  isConfigDirty: boolean;
  isOpen: boolean;
  isSaving: boolean;
  isCommonOrgToggleDisabled?: boolean;
  showCommonOrgToggle?: boolean;
  onChange: (patch: Partial<FeeManagerConfig>) => void;
  onClose: () => void;
  onSave: () => void;
}

export const ConfigurationModal = ({
  config,
  schema,
  isConfigDirty,
  isOpen,
  isSaving,
  isCommonOrgToggleDisabled = false,
  showCommonOrgToggle = false,
  onChange,
  onClose,
  onSave
}: ConfigurationModalProps) => {
  const [activeTab, setActiveTab] = useState<"NEW" | "USED">("NEW");

  const handleHide = ((...args: unknown[]) => {
    const reason = args[1] as string | undefined;
    if (reason === "backdropClick") return;
    onClose();
  }) as () => void;

  const allKeys = Object.keys(schema) as FeeManagerConfigFieldKey[];
  const newKeys = allKeys.filter(k => k.startsWith("NEW_"));
  const usedKeys = allKeys.filter(k => k.startsWith("USED_"));
  const tabKeys = (activeTab === "NEW" ? newKeys : usedKeys).filter(
    k => !SUB_TOGGLE_KEYS.includes(k)
  );

  const renderField = (key: FeeManagerConfigFieldKey) => {
    const field = schema[key];
    if (!field) return null;
    const value = config[key];

    if (field.type === "boolean") {
      return (
        <SwitchRow key={key}>
          <Switch
            checked={value as boolean}
            label={field.label}
            labelAlignment="left"
            labelSx={{ justifyContent: "space-between", width: "100%" }}
            value={value}
            onClick={e => onChange({ [key]: e.target.value === "checked" })}
          />
        </SwitchRow>
      );
    }

    if (field.type === "select") {
      return (
        <FormRow key={key}>
          <SelectInput
            displayLabel
            disabled={field.disabled ? field.disabled(config) : false}
            displayDeselectOption={false}
            id={key}
            label={field.label}
            name={key}
            options={field.options as { label: string; value: string }[]}
            value={value as string}
            onChange={e => onChange({ [key]: e.target.value })}
          />
        </FormRow>
      );
    }

    if (field.type === "input") {
      return (
        <FormRow key={key}>
          <TextInput
            id={key}
            label={field.label}
            name={key}
            type={field.inputType}
            value={String(value ?? "")}
            onChange={e => onChange({ [key]: e.target.value })}
          />
        </FormRow>
      );
    }

    return null;
  };

  const feeSourceKey = `${activeTab}_feeSource`;
  const isCommonOrgChecked = isCommonOrgSource(config[feeSourceKey]);

  const tabs = [
    {
      active: activeTab === "NEW",
      disabled: false,
      label: String(Locale.getText("FEEMANAGER_NEW_TAB")),
      value: 0
    },
    {
      active: activeTab === "USED",
      disabled: false,
      label: String(Locale.getText("FEEMANAGER_PREOWNED_TAB")),
      value: 1
    }
  ];

  return (
    <StyledModal
      header={String(Locale.getText("CONFIGURATION"))}
      show={isOpen}
      size="large"
      onHide={handleHide}
    >
      <Tabs
        id="config-tabs"
        tabs={tabs}
        onActivate={index => setActiveTab(index === 0 ? "NEW" : "USED")}
      />

      <TabContent>
        {showCommonOrgToggle && (
          <SwitchRow>
            <Switch
              checked={isCommonOrgChecked}
              data-testid="config-common-org-source"
              disabled={isCommonOrgToggleDisabled}
              label={String(Locale.getText("FEEMANAGER_COMMON_ORG_SOURCE"))}
              labelAlignment="left"
              labelSx={{ justifyContent: "space-between", width: "100%" }}
              value={isCommonOrgChecked}
              onClick={e =>
                onChange({
                  [feeSourceKey]:
                    e.target.value === "checked"
                      ? FEE_SOURCE_COMMON_ORG
                      : FEE_SOURCE_DDC
                })
              }
            />
          </SwitchRow>
        )}
        {tabKeys.map(renderField)}
      </TabContent>

      <ButtonGroup>
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration close button clicked"
          }}
          buttonStyle="secondary"
          disabled={isSaving}
          onClick={onClose}
        >
          {String(Locale.getText("CLOSE"))}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration save button clicked"
          }}
          buttonStyle="primary"
          disabled={isSaving || !isConfigDirty}
          isLoading={isSaving}
          onClick={onSave}
        >
          {String(Locale.getText("FEEMANAGER_SAVE"))}
        </Button>
      </ButtonGroup>
    </StyledModal>
  );
};
